import { Box, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { LuChevronRight } from "react-icons/lu";

/** iOS-style grouped list. */
export function Group({
  title,
  children,
}: React.PropsWithChildren<{ title?: string }>) {
  return (
    <Box mx={4} mt={6}>
      {title && (
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="faint"
          textTransform="uppercase"
          letterSpacing="0.06em"
          px={2}
          mb={2}
        >
          {title}
        </Text>
      )}
      <Box bg="surface" borderRadius="18px" overflow="hidden">
        {React.Children.toArray(children).map((child, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Box h="1px" bg="line" ml={4} />}
            {child}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

type RowProps = {
  icon?: React.ReactNode;
  label: React.ReactNode;
  detail?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  chevron?: boolean;
  danger?: boolean;
  right?: React.ReactNode;
};

/** One row of a Group. A button, a link (`href`) or plain text. */
export function Row({
  icon,
  label,
  detail,
  onClick,
  href,
  chevron,
  danger,
  right,
}: RowProps) {
  const content = (
    <Flex
      align="center"
      gap={3}
      px={4}
      minH="54px"
      py={2.5}
      _active={onClick || href ? { bg: "elevated" } : undefined}
      transition="background 0.15s"
    >
      {icon && (
        <Flex
          boxSize="30px"
          borderRadius="9px"
          bg="elevated"
          align="center"
          justify="center"
          color={danger ? "red.400" : "rgba(255,255,255,0.85)"}
          flexShrink={0}
        >
          {icon}
        </Flex>
      )}
      <Box flex="1" minW={0} textAlign="left">
        <Text color={danger ? "red.400" : "white"} fontWeight="medium">
          {label}
        </Text>
        {detail && (
          <Text fontSize="sm" color="muted" truncate>
            {detail}
          </Text>
        )}
      </Box>
      {right}
      {chevron && (
        <Box color="faint">
          <LuChevronRight size={18} />
        </Box>
      )}
    </Flex>
  );
  if (href) {
    return (
      <Box
        as="a"
        display="block"
        {...{
          href,
          target: href.startsWith("mailto:") ? undefined : "_blank",
          rel: "noreferrer",
        }}
      >
        {content}
      </Box>
    );
  }
  if (onClick) {
    return (
      <Box as="button" display="block" width="100%" onClick={onClick}>
        {content}
      </Box>
    );
  }
  return <Box>{content}</Box>;
}
